/**
 * =============================================================================
 * SLA CONFIGURATION MODEL - Central SLA Architecture
 * =============================================================================
 * 
 * PHASE 2B: Operational Intelligence Layer
 * 
 * DO NOT hardcode SLA logic into components. This is the central config that
 * drives all SLA calculations, escalations, and alerts.
 * 
 * Supports:
 * - SLA by job type
 * - SLA by priority
 * - SLA by trade
 * - SLA by client/contract (future)
 * 
 * States: Healthy → Warning → Risk → Breached
 */

// =============================================================================
// SLA THRESHOLDS BY PRIORITY
// =============================================================================
export const SLA_THRESHOLDS = {
  emergency: {
    target_hours: 4,       // 4 hours for emergency
    warning_pct: 50,       // Warning at 50% of time elapsed (2h)
    risk_pct: 75,          // Risk at 75% (3h)
    breach_pct: 100,       // Breached at 100% (4h)
    escalation_interval: 30, // Escalate every 30 mins when at risk
  },
  urgent: {
    target_hours: 24,      // 24 hours for urgent
    warning_pct: 50,       // Warning at 12h
    risk_pct: 75,          // Risk at 18h
    breach_pct: 100,       // Breached at 24h
    escalation_interval: 60, // Escalate every 1h when at risk
  },
  high: {
    target_hours: 48,      // 48 hours for high
    warning_pct: 50,
    risk_pct: 75,
    breach_pct: 100,
    escalation_interval: 120,
  },
  medium: {
    target_hours: 72,      // 72 hours (3 days) for medium
    warning_pct: 50,
    risk_pct: 75,
    breach_pct: 100,
    escalation_interval: 240,
  },
  standard: {
    target_hours: 168,     // 7 days for standard
    warning_pct: 50,
    risk_pct: 75,
    breach_pct: 100,
    escalation_interval: 480,
  },
  low: {
    target_hours: 336,     // 14 days for low
    warning_pct: 50,
    risk_pct: 75,
    breach_pct: 100,
    escalation_interval: 720,
  },
  planned: {
    target_hours: 720,     // 30 days for planned
    warning_pct: 50,
    risk_pct: 75,
    breach_pct: 100,
    escalation_interval: 1440,
  },
  ppm: {
    target_hours: 720,     // 30 days for PPM
    warning_pct: 70,       // More lenient for planned work
    risk_pct: 90,
    breach_pct: 100,
    escalation_interval: 1440,
  },
};

// =============================================================================
// SLA STATUS ENUM
// =============================================================================
export const SLA_STATUS = {
  HEALTHY: 'healthy',
  WARNING: 'warning',
  RISK: 'risk',
  BREACHED: 'breached',
  NO_SLA: 'no_sla',
};

// =============================================================================
// SLA STATUS COLORS
// =============================================================================
export const SLA_COLORS = {
  healthy: { 
    bg: '#DCFCE7', 
    text: '#166534', 
    border: '#22C55E',
    badge: 'bg-green-100 text-green-700'
  },
  warning: { 
    bg: '#FEF3C7', 
    text: '#92400E', 
    border: '#F59E0B',
    badge: 'bg-amber-100 text-amber-700'
  },
  risk: { 
    bg: '#FEE2E2', 
    text: '#991B1B', 
    border: '#EF4444',
    badge: 'bg-red-100 text-red-700'
  },
  breached: { 
    bg: '#FEE2E2', 
    text: '#7F1D1D', 
    border: '#DC2626',
    badge: 'bg-red-200 text-red-800'
  },
  no_sla: { 
    bg: '#F3F4F6', 
    text: '#6B7280', 
    border: '#9CA3AF',
    badge: 'bg-slate-100 text-slate-600'
  },
};

// =============================================================================
// PRIORITY DISPLAY CONFIG
// =============================================================================
export const PRIORITY_CONFIG = {
  emergency: { 
    label: 'Emergency', 
    color: '#DC2626', 
    bg: '#FEE2E2',
    order: 1 
  },
  urgent: { 
    label: 'Urgent', 
    color: '#EA580C', 
    bg: '#FFEDD5',
    order: 2 
  },
  high: { 
    label: 'High', 
    color: '#F59E0B', 
    bg: '#FEF3C7',
    order: 3 
  },
  medium: { 
    label: 'Medium', 
    color: '#3B82F6', 
    bg: '#DBEAFE',
    order: 4 
  },
  standard: { 
    label: 'Standard', 
    color: '#6B7280', 
    bg: '#F3F4F6',
    order: 5 
  },
  low: { 
    label: 'Low', 
    color: '#9CA3AF', 
    bg: '#F9FAFB',
    order: 6 
  },
  planned: { 
    label: 'Planned', 
    color: '#8B5CF6', 
    bg: '#EDE9FE',
    order: 7 
  },
  ppm: { 
    label: 'PPM', 
    color: '#0D9488', 
    bg: '#CCFBF1',
    order: 8 
  },
};

// =============================================================================
// SLA CALCULATION FUNCTIONS
// =============================================================================

/**
 * Calculate SLA status for a job
 * @param {Object} job - Job object with sla_due_at, created_at, priority
 * @returns {Object} - { status, percentElapsed, hoursRemaining, hoursOverdue, thresholds }
 */
export function calculateSLAStatus(job) {
  if (!job.sla_due_at) {
    return {
      status: SLA_STATUS.NO_SLA,
      percentElapsed: 0,
      hoursRemaining: null,
      hoursOverdue: null,
      thresholds: null,
    };
  }

  const now = new Date();
  const due = new Date(job.sla_due_at);
  const created = new Date(job.created_at || now);
  
  const totalMs = due - created;
  const elapsedMs = now - created;
  const percentElapsed = Math.max(0, (elapsedMs / totalMs) * 100);
  
  const hoursRemaining = Math.max(0, (due - now) / (1000 * 60 * 60));
  const hoursOverdue = now > due ? Math.abs((now - due) / (1000 * 60 * 60)) : 0;

  // Get thresholds based on priority
  const priority = (job.priority || 'medium').toLowerCase();
  const thresholds = SLA_THRESHOLDS[priority] || SLA_THRESHOLDS.medium;

  // Determine status
  let status;
  if (now > due) {
    status = SLA_STATUS.BREACHED;
  } else if (percentElapsed >= thresholds.risk_pct) {
    status = SLA_STATUS.RISK;
  } else if (percentElapsed >= thresholds.warning_pct) {
    status = SLA_STATUS.WARNING;
  } else {
    status = SLA_STATUS.HEALTHY;
  }

  return {
    status,
    percentElapsed: Math.min(100, percentElapsed),
    hoursRemaining: Math.round(hoursRemaining * 10) / 10,
    hoursOverdue: Math.round(hoursOverdue * 10) / 10,
    thresholds,
  };
}

/**
 * Get display info for SLA status
 */
export function getSLADisplayInfo(status) {
  const labels = {
    [SLA_STATUS.HEALTHY]: 'Healthy',
    [SLA_STATUS.WARNING]: 'Warning',
    [SLA_STATUS.RISK]: 'At Risk',
    [SLA_STATUS.BREACHED]: 'Breached',
    [SLA_STATUS.NO_SLA]: 'No SLA',
  };

  return {
    label: labels[status] || 'Unknown',
    colors: SLA_COLORS[status] || SLA_COLORS.no_sla,
  };
}

/**
 * Categorize jobs by priority and SLA status for heatmap
 * @param {Array} jobs - Array of job objects
 * @returns {Object} - Matrix of { [priority]: { [status]: jobs[] } }
 */
export function categorizeJobsForHeatmap(jobs) {
  const priorities = ['emergency', 'urgent', 'high', 'medium', 'standard', 'low', 'planned', 'ppm'];
  const statuses = [SLA_STATUS.HEALTHY, SLA_STATUS.WARNING, SLA_STATUS.RISK, SLA_STATUS.BREACHED];
  
  // Initialize matrix
  const matrix = {};
  priorities.forEach(p => {
    matrix[p] = {};
    statuses.forEach(s => {
      matrix[p][s] = [];
    });
  });

  // Categorize jobs
  jobs.forEach(job => {
    const priority = (job.priority || 'medium').toLowerCase();
    const normalizedPriority = priorities.includes(priority) ? priority : 'medium';
    const slaInfo = calculateSLAStatus(job);
    
    if (slaInfo.status !== SLA_STATUS.NO_SLA && matrix[normalizedPriority]) {
      matrix[normalizedPriority][slaInfo.status]?.push(job);
    }
  });

  return matrix;
}

/**
 * Get aggregate SLA metrics
 */
export function getSLAMetrics(jobs) {
  let healthy = 0, warning = 0, risk = 0, breached = 0, noSla = 0;
  let totalHoursOverdue = 0;
  
  jobs.forEach(job => {
    const sla = calculateSLAStatus(job);
    switch (sla.status) {
      case SLA_STATUS.HEALTHY: healthy++; break;
      case SLA_STATUS.WARNING: warning++; break;
      case SLA_STATUS.RISK: risk++; break;
      case SLA_STATUS.BREACHED: 
        breached++; 
        totalHoursOverdue += sla.hoursOverdue || 0;
        break;
      default: noSla++; break;
    }
  });

  const jobsWithSla = healthy + warning + risk + breached;
  const slaCompliance = jobsWithSla > 0 ? ((healthy + warning) / jobsWithSla * 100).toFixed(1) : 100;

  return {
    healthy,
    warning,
    risk,
    breached,
    noSla,
    total: jobs.length,
    jobsWithSla,
    slaCompliance,
    avgHoursOverdue: breached > 0 ? (totalHoursOverdue / breached).toFixed(1) : 0,
    atRiskCount: warning + risk + breached,
  };
}

export default {
  SLA_THRESHOLDS,
  SLA_STATUS,
  SLA_COLORS,
  PRIORITY_CONFIG,
  calculateSLAStatus,
  getSLADisplayInfo,
  categorizeJobsForHeatmap,
  getSLAMetrics,
};
